import React from 'react';
import '../../../css/common/utility/ErrorBoundary.css';

/**
 * ErrorBoundary Component
 *
 * Purpose:
 * Catches render-time errors from anywhere in the tree below it so a single
 * failure degrades to a readable message instead of unmounting the whole app
 * and leaving a blank page.
 *
 * Features:
 * - Renders a recovery message with a link back to the home page.
 * - Logs the error and component stack for diagnosis.
 *
 * Used In:
 * Wraps the router in src/index.js.
 *
 * Note:
 * Must be a class component. React has no hook equivalent of
 * getDerivedStateFromError / componentDidCatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Unhandled render error:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="errorBoundary" role="alert">
        <h1>Something went wrong</h1>
        <p>
          Sorry, this page could not be displayed. Please try reloading, or head
          back to the home page.
        </p>
        {/* A full reload rather than a router link: the router itself may be
            the thing that failed. */}
        <a className="errorBoundary-link" href="/">
          Go to the home page
        </a>
      </div>
    );
  }
}

export default ErrorBoundary;
